import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from './supabase';
import type { Database, Json } from './database.types';

/**
 * Enquiries: reading and working `leads`, its timeline (`lead_events`) and
 * the saved replies. Every write here is permitted by RLS only for staff
 * (migration 20261013130000_desk_enquiries.sql); status and follow-up
 * changes write their own timeline entries in the database.
 */

export type Lead = Database['public']['Tables']['leads']['Row'];
export type LeadEvent = Database['public']['Tables']['lead_events']['Row'];
export type Template = Database['public']['Tables']['message_templates']['Row'];

export type LeadStatus =
  | 'new'
  | 'contacted'
  | 'scheduled'
  | 'qualified'
  | 'proposal'
  | 'converted'
  | 'lost'
  | 'spam';

/** The order a lead moves through, and the words Desk shows for each. */
export const STATUSES: { value: LeadStatus; label: string; chip: string }[] = [
  { value: 'new', label: 'New', chip: 'chip-new' },
  { value: 'contacted', label: 'Contacted', chip: '' },
  { value: 'scheduled', label: 'Call booked', chip: '' },
  { value: 'qualified', label: 'Qualified', chip: 'chip-warn' },
  { value: 'proposal', label: 'Proposal sent', chip: 'chip-warn' },
  { value: 'converted', label: 'Won', chip: 'chip-ok' },
  { value: 'lost', label: 'Lost', chip: '' },
  { value: 'spam', label: 'Spam', chip: 'chip-alert' },
];

export const statusOf = (s: string) => STATUSES.find((x) => x.value === s) ?? STATUSES[0];

/** Still being worked: shown by default, counted on Today. */
export const OPEN_STATUSES: LeadStatus[] = ['new', 'contacted', 'scheduled', 'qualified', 'proposal'];

export interface LeadMeta {
  interest?: string | null;
  source_page?: string | null;
}
export const metaOf = (l: Pick<Lead, 'metadata'>) =>
  (l.metadata && typeof l.metadata === 'object' && !Array.isArray(l.metadata) ? l.metadata : {}) as LeadMeta;

export const SOURCES: { value: string; label: string }[] = [
  { value: 'website', label: 'Website form' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'call', label: 'Phone call' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'referral', label: 'Referral' },
  { value: 'walk-in', label: 'In person' },
  { value: 'other', label: 'Other' },
];
export const sourceLabel = (s: string) => SOURCES.find((x) => x.value === s)?.label ?? s;

/* ------------------------------------------------------------------ reads */

export const leadKeys = {
  all: ['leads'] as const,
  list: (scope: string) => ['leads', 'list', scope] as const,
  one: (id: string) => ['leads', 'one', id] as const,
  events: (id: string) => ['leads', 'events', id] as const,
  counts: ['leads', 'counts'] as const,
  templates: ['templates'] as const,
};

export type LeadScope = 'open' | 'won' | 'closed' | 'all';

export function useLeads(scope: LeadScope) {
  return useQuery({
    queryKey: leadKeys.list(scope),
    queryFn: async () => {
      let q = supabase.from('leads').select('*').order('created_at', { ascending: false }).limit(500);
      if (scope === 'open') q = q.in('status', OPEN_STATUSES);
      if (scope === 'won') q = q.eq('status', 'converted');
      if (scope === 'closed') q = q.in('status', ['lost', 'spam']);
      const { data, error } = await q;
      if (error) throw error;
      return data;
    },
  });
}

export function useLead(id: string) {
  return useQuery({
    queryKey: leadKeys.one(id),
    queryFn: async () => {
      const { data, error } = await supabase.from('leads').select('*').eq('id', id).maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

export function useLeadEvents(id: string) {
  return useQuery({
    queryKey: leadKeys.events(id),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('lead_events')
        .select('*')
        .eq('lead_id', id)
        .order('at', { ascending: false });
      if (error) throw error;
      return data;
    },
  });
}

/** What Today and the nav badge show: new leads, and follow-ups that are due. */
export function useWaitingCounts() {
  return useQuery({
    queryKey: leadKeys.counts,
    queryFn: async () => {
      const [fresh, due] = await Promise.all([
        supabase.from('leads').select('id', { count: 'exact', head: true }).eq('status', 'new'),
        supabase
          .from('leads')
          .select('id', { count: 'exact', head: true })
          .in('status', OPEN_STATUSES)
          .lte('next_action_at', new Date().toISOString()),
      ]);
      if (fresh.error) throw fresh.error;
      if (due.error) throw due.error;
      return { newLeads: fresh.count ?? 0, dueFollowUps: due.count ?? 0 };
    },
    refetchInterval: 5 * 60_000,
  });
}

export function useTemplates() {
  return useQuery({
    queryKey: leadKeys.templates,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('message_templates')
        .select('*')
        .eq('archived', false)
        .order('position');
      if (error) throw error;
      return data;
    },
    staleTime: 10 * 60_000,
  });
}

/* ----------------------------------------------------------------- writes */

export function useUpdateLead(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Database['public']['Tables']['leads']['Update']) => {
      const { data, error } = await supabase.from('leads').update(patch).eq('id', id).select('*').single();
      if (error) throw error;
      return data;
    },
    onSuccess: (lead) => {
      qc.setQueryData(leadKeys.one(id), lead);
      qc.invalidateQueries({ queryKey: leadKeys.all });
    },
  });
}

export function useAddEvent(leadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (e: { kind: 'note' | 'call' | 'whatsapp'; body?: string; meta?: Json }) => {
      const { error } = await supabase.from('lead_events').insert({ lead_id: leadId, ...e });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: leadKeys.events(leadId) }),
  });
}

export function useCreateLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (lead: Database['public']['Tables']['leads']['Insert']) => {
      const { data, error } = await supabase.from('leads').insert(lead).select('id').single();
      if (error) throw error;
      return data.id;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: leadKeys.all }),
  });
}

/* --------------------------------------------------------------- realtime */

/**
 * New leads and timeline entries arrive without a reload. Realtime honours
 * RLS, so only a staff session receives anything.
 */
export function useLeadsLive() {
  const qc = useQueryClient();
  useEffect(() => {
    const channel = supabase
      .channel('desk-leads')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'leads' }, () =>
        qc.invalidateQueries({ queryKey: leadKeys.all }),
      )
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'lead_events' }, (payload) => {
        const leadId = (payload.new as LeadEvent).lead_id;
        qc.invalidateQueries({ queryKey: leadKeys.events(leadId) });
      })
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [qc]);
}
